import Autocomplete, {
  AutocompleteChangeReason,
  AutocompleteValue,
  createFilterOptions,
} from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import { ReactElement, SyntheticEvent, useRef, useState } from 'react';
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  useFormContext,
} from 'react-hook-form';

import { useFormProviderRCPropsContext } from '@/components/form/FormProviderRC.comp.tsx';
import {
  SelectOptionIdType,
  SelectSearchOptionModel,
} from '@/components/form/model/SelectSearchOption.model.ts';
import { handleKeyDownDefault } from '@/components/form/util/handleKeyDownDefault.util.ts';
import { isFormFieldRequired } from '@/utils/form/isFormFieldRequired.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

import { FormPropsModel } from './model/FormProps.model';
import { TextError } from './TextError.comp';

export type SelectSearchFormFieldOnChangeHandler<
  ID_TYPE extends SelectOptionIdType = number,
  IS_MULTI extends boolean = false,
> = (
  newValue: SelectSearchFormFieldValue<ID_TYPE, IS_MULTI>,
  reason: AutocompleteChangeReason,
) => void;

export type SelectSearchFormFieldValue<
  ID_TYPE extends SelectOptionIdType = number,
  IS_MULTI extends boolean = false,
> = IS_MULTI extends true
  ? SelectSearchOptionModel<ID_TYPE>[]
  : SelectSearchOptionModel<ID_TYPE> | null;

type AutocompleteOption<ID_TYPE extends SelectOptionIdType> =
  CreateOption | SelectSearchOptionModel<ID_TYPE>;
type BasicProps<ID_TYPE extends SelectOptionIdType = number, IS_MULTI extends boolean = false> = {
  closeMenuOnSelect?: boolean;
  formatCreateLabel?: (inputValue: string) => ReactElement;
  hideSelectedOptions?: boolean;
  isMulti?: IS_MULTI;
  isNotClearable?: boolean;
  isOptionDisabled?: (option: SelectSearchOptionModel<ID_TYPE>) => boolean;
  menuPlacement?: MenuPlacement;
  onChange?: SelectSearchFormFieldOnChangeHandler<ID_TYPE, IS_MULTI>;
};
type CreateOption = { inputValue: string };

type MenuPlacement = 'auto' | 'bottom' | 'top';

type Props<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  ID_TYPE extends SelectOptionIdType = number,
  IS_MULTI extends boolean = false,
> = FormPropsModel<TName> &
  BasicProps<ID_TYPE, IS_MULTI> &
  (
    | {
        isCreatable: true;
        onCreate: (inputValue: string) => void;
      }
    | {
        isCreatable?: false;
        onCreate?: undefined;
      }
  ) &
  (
    | {
        isAsync: true;
        loadOptions: (inputValue: string) => Promise<SelectSearchOptionModel<ID_TYPE>[]>;
      }
    | {
        isAsync?: false;
        loadOptions?: undefined;
        options: ReadonlyArray<SelectSearchOptionModel<ID_TYPE>>;
      }
  );

const isCreateOption = (option: unknown): option is CreateOption => {
  return typeof option === 'object' && option !== null && 'inputValue' in option;
};

const getCreatedValue = (value: unknown): string | undefined => {
  const values = Array.isArray(value) ? value : [value];
  const createdOption = values.find(option => typeof option === 'string' || isCreateOption(option));

  if (typeof createdOption === 'string') {
    return createdOption;
  }

  return isCreateOption(createdOption) ? createdOption.inputValue : undefined;
};

const getPopperPlacement = (menuPlacement?: MenuPlacement) => {
  if (menuPlacement === 'top') {
    return 'top-start' as const;
  }
  if (menuPlacement === 'bottom') {
    return 'bottom-start' as const;
  }
  return undefined;
};

export const SelectSearchFormField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  ID_TYPE extends SelectOptionIdType = number,
  IS_MULTI extends boolean = false,
>(
  props: Props<TFieldValues, TName, ID_TYPE, IS_MULTI>,
): ReactElement<Props<TFieldValues, TName, ID_TYPE, IS_MULTI>> => {
  const {
    closeMenuOnSelect,
    disabled,
    formatCreateLabel,
    fullWidth,
    hideSelectedOptions,
    id,
    isAsync,
    isCreatable,
    isMulti,
    isNotClearable,
    isOptionDisabled,
    label,
    menuPlacement,
    name,
    onChange,
    onCreate,
    placeholder,
    sx,
    title,
  } = props;
  const { control } = useFormContext<TFieldValues>();
  const { validation } = useFormProviderRCPropsContext();
  const { t } = useTranslationWithPrefix('components.form.select');
  const [asyncOptions, setAsyncOptions] = useState<SelectSearchOptionModel<ID_TYPE>[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const requestIdRef = useRef(0);

  const render: ControllerProps<TFieldValues, TName>['render'] = ({
    field,
    fieldState: { error },
  }) => {
    const isRequired = isFormFieldRequired(field.name, validation);
    let options: ReadonlyArray<SelectSearchOptionModel<ID_TYPE>> = [];
    if (isAsync) {
      options = asyncOptions;
    } else if ('options' in props) {
      options = props.options;
    }
    const defaultFilterOptions = createFilterOptions<AutocompleteOption<ID_TYPE>>();
    let normalizedValue: unknown = field.value ?? null;
    if (isMulti && !Array.isArray(normalizedValue)) {
      normalizedValue = [];
    }
    if (isMulti && Array.isArray(normalizedValue)) {
      normalizedValue = normalizedValue.filter(option => option != null);
    }
    const value = normalizedValue as AutocompleteValue<
      AutocompleteOption<ID_TYPE>,
      IS_MULTI,
      boolean,
      boolean
    >;

    const getOptionLabel = (option: AutocompleteOption<ID_TYPE> | string) => {
      if (!option) {
        return '';
      }
      if (typeof option === 'string') {
        return option;
      }
      if (isCreateOption(option)) {
        return option.inputValue;
      }
      return option.label ?? '';
    };

    const handleInputChange = (_event: SyntheticEvent, newInputValue: string, reason: string) => {
      const nextInputValue = typeof newInputValue === 'string' ? newInputValue : '';
      setInputValue(nextInputValue);
      if (!isAsync || reason !== 'input' || !('loadOptions' in props)) {
        return;
      }

      const currentRequestId = ++requestIdRef.current;
      setIsLoading(true);
      void props
        .loadOptions(nextInputValue)
        .then(newOptions => {
          if (currentRequestId === requestIdRef.current) {
            setAsyncOptions(newOptions);
          }
        })
        .catch(() => {
          if (currentRequestId === requestIdRef.current) {
            setAsyncOptions([]);
          }
        })
        .finally(() => {
          if (currentRequestId === requestIdRef.current) {
            setIsLoading(false);
          }
        });
    };

    const handleOnChange = (
      _event: SyntheticEvent,
      newValue: AutocompleteValue<AutocompleteOption<ID_TYPE>, IS_MULTI, boolean, boolean>,
      reason: AutocompleteChangeReason,
    ) => {
      const createdValue = getCreatedValue(newValue);
      if (createdValue !== undefined) {
        setInputValue('');
        onCreate?.(createdValue);
        return;
      }

      if (isMulti) {
        const selectedValues = (Array.isArray(newValue) ? newValue : []).filter(
          (option): option is SelectSearchOptionModel<ID_TYPE> =>
            !isCreateOption(option) && typeof option !== 'string',
        );
        onChange?.(selectedValues as SelectSearchFormFieldValue<ID_TYPE, IS_MULTI>, reason);
        field.onChange(selectedValues);
        return;
      }

      const selectedValue =
        !Array.isArray(newValue) && !isCreateOption(newValue) && typeof newValue !== 'string'
          ? newValue
          : null;
      onChange?.(selectedValue as SelectSearchFormFieldValue<ID_TYPE, IS_MULTI>, reason);
      field.onChange(selectedValue);
    };

    return (
      <FormControl
        fullWidth={fullWidth ?? true}
        sx={{
          '& .MuiAutocomplete-root .MuiAutocomplete-inputRoot': {
            flexWrap: 'nowrap',
            height: 40,
            overflow: 'hidden',
          },
          height: '100%',
        }}
      >
        <Autocomplete<AutocompleteOption<ID_TYPE>, IS_MULTI, boolean, boolean>
          disableClearable={isNotClearable === true}
          disableCloseOnSelect={closeMenuOnSelect === false}
          disabled={disabled}
          filterOptions={(availableOptions, state) => {
            const filteredOptions = isAsync
              ? availableOptions
              : defaultFilterOptions(availableOptions, state);
            const normalizedInputValue = state.inputValue.trim().toLowerCase();
            const hasMatchingOption = availableOptions.some(
              option =>
                !isCreateOption(option) &&
                option.label.trim().toLowerCase() === normalizedInputValue,
            );

            if (!isCreatable || !normalizedInputValue || hasMatchingOption) {
              return filteredOptions;
            }

            return [...filteredOptions, { inputValue: state.inputValue }];
          }}
          filterSelectedOptions={hideSelectedOptions ?? Boolean(isMulti)}
          freeSolo={isCreatable}
          fullWidth={fullWidth ?? true}
          getOptionDisabled={option =>
            !isCreateOption(option) && (isOptionDisabled?.(option) ?? false)
          }
          getOptionLabel={getOptionLabel}
          id={id}
          isOptionEqualToValue={(option, optionValue) =>
            typeof option !== 'string' &&
            !isCreateOption(option) &&
            optionValue != null &&
            !isCreateOption(optionValue) &&
            typeof optionValue !== 'string' &&
            option.value === optionValue.value
          }
          limitTags={isMulti ? 1 : -1}
          loading={isLoading}
          loadingText={t('loading')}
          multiple={isMulti}
          noOptionsText={inputValue ? t('noOptionsMessage') : t('typeToSearch')}
          onBlur={field.onBlur}
          onChange={handleOnChange}
          onInputChange={handleInputChange}
          onKeyDown={handleKeyDownDefault({ allowEnterForSelect: true })}
          openOnFocus
          options={options}
          renderInput={params => (
            <TextField
              {...params}
              error={Boolean(error)}
              inputRef={field.ref}
              label={label}
              name={field.name}
              placeholder={placeholder ?? label}
              size="small"
              slotProps={{
                ...params.slotProps,
                inputLabel: { ...params.slotProps.inputLabel, required: isRequired, shrink: true },
              }}
              variant="standard"
            />
          )}
          renderOption={(optionProps, option) => {
            const { key, ...optionPropsWithoutKey } = optionProps;
            if (isCreateOption(option)) {
              return (
                <li key={key} {...optionPropsWithoutKey}>
                  {formatCreateLabel?.(option.inputValue) ??
                    t('createItem', { item: option.inputValue })}
                </li>
              );
            }

            return (
              <li key={key} {...optionPropsWithoutKey}>
                {option.label}
              </li>
            );
          }}
          slotProps={{
            listbox: { sx: { maxHeight: 200, p: 0.5 } },
            popper: {
              placement: getPopperPlacement(menuPlacement),
              style: { zIndex: 1500 },
            },
          }}
          sx={sx}
          title={title}
          value={value}
        />
        <TextError error={error} />
      </FormControl>
    );
  };

  return <Controller control={control} name={name} render={render} />;
};
